import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { WizardValidationError } from '../types/Wizard';
import { RapidStorSettings } from './rapidstor';

export const validatePassword = (password: string): boolean => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,20}$/;
    return passwordRegex.test(password);
};

export const validateString = (value: string): boolean => {
    if (value === '') return false;
    if (value === undefined) return false;
    if (value === null) return false;
    if (value.trim() === '') return false;
    if (!value) return false;

    return true;
};

// Autobill BSB rule: exactly 6 digits (the input never permits separators).
export const validateBsbNumber = (value: string): boolean => /^\d{6}$/.test(value ?? '');

// Legacy autobill account-number rule: 6–8 digits, no separators.
export const validateAutobillAccountNumber = (value: string): boolean => /^\d{6,8}$/.test(value ?? '');

export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email);
};

export const excludedInputs = (location: Location): string[] => {
    const excl: string[] = [
        'cardType',
        'boxShopCouponId',
        'pickupLocation',
        'autobill',
        // Autobill validation is owned by validateAutobill(), not the generic key loop.
        'autobillType',
        'autobillBsbNumber',
        'autobillAccountNumber',
        'autobillCreditCardExpiryMonth',
        'autobillCreditCardExpiryYear',
        'autobillCreditCardNumber',
        'autobillCreditCardCsv',
        'autobillCreditCardName',
        'bank',
    ];

    if (!RapidStorSettings(location).alternateContact().show()) {
        excl.push('title_alternate');
        excl.push('firstName_alternate');
        excl.push('lastName_alternate');
        excl.push('phone_alternate');
        excl.push('email_alternate');
        excl.push('address_alternate');
        excl.push('city_alternate');
        excl.push('state_alternate');
        excl.push('postcode_alternate');
    }

    if (!RapidStorSettings(location).dateOfBirth().show()) {
        excl.push('dateOfBirth');
    }

    if (location.rapidstor.wizards.moveinSingle.hideDriversLicenceState) {
        excl.push('driverLicenseState');
    }

    if (location.rapidstor.wizards.moveinSingle.hideLicencePlate || window.rapidstor_settings.rapidstor_options.checkoutVariant === 'simplified') {
        excl.push('licencePlate');
    }

    if (
        location.rapidstor.wizards.moveinSingle.hideDriversLicence
    || !location.rapidstor.wizards.moveinSingle.allowIdUpload
    ) {
        excl.push('driverLicenseNumber');
        excl.push('driverLicenseState');
        excl.push('idDocumentNumber');
    }

    if (!RapidStorSettings(location).postalCode().show()) {
        excl.push('postcode');
        excl.push('postcode_alternate');
    }

    if (!RapidStorSettings(location).state().show()) {
        excl.push('state');
        excl.push('state_alternate');
    }

    if (!RapidStorSettings(location).insurance().manualEntry().required()) {
        excl.push('declaredValueOfGoods');
    }

    if (RapidStorSettings(location).insurance().optional()) {
        excl.push('insuranceCoverage');
    }

    if (!RapidStorSettings(location).paymentForm().show()) {
        excl.push('cardHolderName');
        excl.push('cardNumber');
        excl.push('cardExpiryMonth');
        excl.push('cardExpiryYear');
        excl.push('cardCsv');
    }

    if (!RapidStorSettings(location).passwordFields().show()) {
        excl.push('password');
        excl.push('confirmPassword');
    }

    if (!RapidStorSettings(location).alternateContact().email().required()) {
        excl.push('email_alternate');
    }

    if (
        !RapidStorSettings(location).alternateContact().phoneNumber().required()
    ) {
        excl.push('phone_alternate');
    }

    if (
        !RapidStorSettings(location).alternateContact().addressDetails().required()
    ) {
        excl.push('address_alternate');
        excl.push('city_alternate');
        excl.push('state_alternate');
        excl.push('postcode_alternate');
    }

    return excl.filter((item, index) => excl.indexOf(item) === index);
};

export const normaliseFieldName = (name: string): string => {
    const spaced = name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    const split = spaced.replace(/([A-Z])/g, ' $1');

    return split.replace(/\s{2,}/g, ' ');
};

export const getAutobillFields = (
    location: Location,
    autobillType: string,
    usePaymentDetails = true,
) => {
    const fields: string[] = ['bank'];

    // Mirrors the legacy single-page wizard: direct-debit fields are required whenever
    // direct debit is available and selected (allowDirectDebit && autobill.type === 2),
    // independent of whether autobill itself is required.
    if (autobillType === 'dd' && RapidStorSettings(location).autobill().allowDirectDebit()) {
        fields.push(
            'autobillAccountType',
            'autobillBsbNumber',
            'autobillAccountNumber',
        );
    } else if (
        autobillType === 'cc'
        && !usePaymentDetails
        && RapidStorSettings(location).autobill().allowCreditCard()
    ) {
        // Autobill on a separate credit card (Use Payment Details deselected). When payment
        // details are reused the main payment card covers these, so they aren't listed here.
        fields.push(
            'autobillCreditCardName',
            'autobillCreditCardNumber',
            'autobillCreditCardExpiryMonth',
            'autobillCreditCardExpiryYear',
            'autobillCreditCardCsv',
        );
    }

    return fields;
};

/**
 * All autobill validation for the move-in wizard, as a pure function so every
 * settings/form combination is unit-testable. Mirrors the legacy single-page wizard.
 *
 * @param requiredMessage translated "autobill required" message (simplified variant).
 */
export const validateAutobill = (
    location: Location,
    form: Record<string, string>,
    isSimplified: boolean,
    requiredMessage: string,
): WizardValidationError[] => {
    const errors: WizardValidationError[] = [];
    const autobill = RapidStorSettings(location).autobill();

    // Autobill is off entirely under a payment gateway or when disabled.
    if (!autobill.show()) {
        return errors;
    }

    // A method must be chosen when autobill is required. The error targets each variant's
    // display control (the "use payment details" checkbox in simplified, the radios in standard).
    if (autobill.required() && form.autobillType === '') {
        errors.push(
            isSimplified
                ? { name: 'autobillUsePaymentDetails', message: requiredMessage }
                : { name: 'autobillType', message: `${normaliseFieldName('autobillType')} is required` },
        );
    }

    // Field-content only applies to the standard variant; simplified reuses the main
    // payment card and has no autobill-specific fields to fill in.
    if (!isSimplified && form.autobillType !== '') {
        const fields = getAutobillFields(
            location,
            form.autobillType,
            form.autobillUsePaymentDetails === 'true',
        );

        for (const field of fields.filter((f) => f !== 'bank')) {
            if (!validateString(form[field])) {
                errors.push({
                    name: field,
                    message: `${normaliseFieldName(field)} is required`,
                });
            }
        }

        // Legacy direct-debit format rules: BSB exactly 6 digits, account 6–8 digits.
        if (form.autobillType === 'dd' && autobill.allowDirectDebit()) {
            if (
                validateString(form.autobillBsbNumber)
                && !validateBsbNumber(form.autobillBsbNumber)
            ) {
                errors.push({ name: 'autobillBsbNumber', message: 'BSB must be 6 digits' });
            }

            if (
                validateString(form.autobillAccountNumber)
                && !validateAutobillAccountNumber(form.autobillAccountNumber)
            ) {
                errors.push({
                    name: 'autobillAccountNumber',
                    message: 'Account number must be 6 to 8 digits',
                });
            }
        }
    }

    return errors;
};
